
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  User,
  signInWithCredential,
  connectAuthEmulator
} from "firebase/auth";
import { 
  getFirestore, 
  enableIndexedDbPersistence, 
  connectFirestoreEmulator,
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { toast } from "@/utils/toast";

// Firebase configuration - consider using environment variables in production
const firebaseConfig = {
  apiKey: "AIzaSyDaSAM4VllUEwP_1QbQHxKz5zAKipI35j4",
  authDomain: "chatrix-web.firebaseapp.com",
  projectId: "chatrix-web",
  storageBucket: "chatrix-web.firebasestorage.app",
  messagingSenderId: "707841029423",
  appId: "1:707841029423:web:769a2620f907b4ff86ddda",
  measurementId: "G-XP9HJJEB6B"
};

// Initialize Firebase services
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const analytics = getAnalytics(app);
const storage = getStorage(app);
const functions = getFunctions(app);

// Enable offline persistence for Firestore (wrapped in try/catch to handle browser limitations)
try {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn('Firestore persistence could not be enabled: multiple tabs open');
    } else if (err.code === 'unimplemented') {
      console.warn('Firestore persistence is not available in this browser');
    }
  });
} catch (error) {
  console.warn('Error setting up Firestore persistence:', error);
}

// Connect to emulators in development environment
const connectToEmulators = () => {
  if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, 'localhost', 8080);
      connectStorageEmulator(storage, 'localhost', 9199);
      connectFunctionsEmulator(functions, 'localhost', 5001);
      console.log('Connected to Firebase emulators');
    } catch (error) {
      console.error('Failed to connect to emulators:', error);
    }
  }
};

// Call this during app initialization if you want to use emulators
// connectToEmulators();

// Authentication state management
export const onUserStateChanged = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, (user) => {
    if (user) {
      // Log anonymous analytics event
      logEvent(analytics, 'user_login', {
        method: user.providerData[0]?.providerId || 'unknown'
      });
    }
    callback(user);
  });
};

export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Authentication functions with improved error handling
export const loginWithEmail = async (email: string, password: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    logEvent(analytics, 'login', { method: 'email' });
    toast.success("Logged in successfully!");
    return result;
  } catch (error: any) {
    let errorMessage = 'Authentication failed';
    
    // Provide more user-friendly error messages
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled';
        break;
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        errorMessage = 'Invalid email or password';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many unsuccessful login attempts. Please try again later';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      default:
        errorMessage = error.message || 'Authentication failed';
    }
    
    toast.error(errorMessage);
    throw error;
  }
};

export const signupWithEmail = async (email: string, password: string, displayName?: string) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update profile with display name if provided
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
      
      // Create initial user document in Firestore
      await createUserProfile(result.user.uid, {
        displayName,
        email: result.user.email || '',
        createdAt: new Date().toISOString(),
        photoURL: result.user.photoURL || '',
        lastActive: new Date().toISOString(),
        preferredLanguage: 'en'
      });
    }
    
    logEvent(analytics, 'sign_up', { method: 'email' });
    toast.success("Account created successfully!");
    return result;
  } catch (error: any) {
    let errorMessage = 'Failed to create account';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already in use';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password is too weak';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      default:
        errorMessage = error.message || 'Failed to create account';
    }
    
    toast.error(errorMessage);
    throw error;
  }
};

// User profile management
export const createUserProfile = async (uid: string, profileData: any) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        ...profileData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (uid: string) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, profileData: any) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...profileData,
      updatedAt: new Date().toISOString()
    });
    
    // Update Firebase auth display name if provided
    if (profileData.displayName && auth.currentUser) {
      await updateProfile(auth.currentUser, { 
        displayName: profileData.displayName,
        photoURL: profileData.photoURL || auth.currentUser.photoURL
      });
    }
    
    // If photoURL is provided, update auth profile
    if (profileData.photoURL && auth.currentUser) {
      await updateProfile(auth.currentUser, {
        photoURL: profileData.photoURL
      });
    }
    
    toast.success("Profile updated successfully!");
  } catch (error) {
    console.error('Error updating user profile:', error);
    toast.error("Failed to update profile");
    throw error;
  }
};

// Phone authentication
export const setupPhoneAuth = async (phoneNumber: string): Promise<string> => {
  try {
    // Set up reCAPTCHA verifier
    const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved, allow signInWithPhoneNumber.
      }
    });
    
    // Add invisible recaptcha container if it doesn't exist
    if (!document.getElementById('recaptcha-container')) {
      const recaptchaContainer = document.createElement('div');
      recaptchaContainer.id = 'recaptcha-container';
      recaptchaContainer.style.display = 'none';
      document.body.appendChild(recaptchaContainer);
    }
    
    // Send verification code
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    
    logEvent(analytics, 'phone_auth_verification_sent');
    toast.success("Verification code sent!");
    
    // Return the verification ID
    return confirmationResult.verificationId;
  } catch (error: any) {
    let errorMessage = 'Failed to send verification code';
    
    switch (error.code) {
      case 'auth/invalid-phone-number':
        errorMessage = 'Please enter a valid phone number';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many requests. Please try again later';
        break;
      case 'auth/captcha-check-failed':
        errorMessage = 'reCAPTCHA verification failed. Please try again';
        break;
      default:
        errorMessage = error.message || 'Failed to send verification code';
    }
    
    toast.error(errorMessage);
    throw error;
  }
};

export const verifyPhoneAuthCode = async (verificationId: string, verificationCode: string) => {
  try {
    // Create credential with the verification ID and code
    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    
    // Sign in with the credential
    const result = await signInWithCredential(auth, credential);
    
    // If no user profile exists, create one
    if (result.user) {
      const userProfile = await getUserProfile(result.user.uid);
      if (!userProfile) {
        await createUserProfile(result.user.uid, {
          displayName: result.user.displayName || 'User',
          email: result.user.email || '',
          phoneNumber: result.user.phoneNumber || '',
          createdAt: new Date().toISOString()
        });
      }
    }
    
    logEvent(analytics, 'login', { method: 'phone' });
    toast.success("Logged in successfully!");
    return result;
  } catch (error: any) {
    let errorMessage = 'Failed to verify code';
    
    switch (error.code) {
      case 'auth/invalid-verification-code':
        errorMessage = 'Invalid verification code';
        break;
      case 'auth/code-expired':
        errorMessage = 'Verification code has expired';
        break;
      default:
        errorMessage = error.message || 'Failed to verify code';
    }
    
    toast.error(errorMessage);
    throw error;
  }
};

// Password management
export const resetPasswordEmail = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    logEvent(analytics, 'password_reset_email_sent');
    toast.success("Password reset email sent successfully!");
    return true;
  } catch (error: any) {
    let errorMessage = 'Failed to send reset email';
    
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address';
        break;
      case 'auth/user-not-found':
        errorMessage = 'No account found with this email';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many requests. Please try again later';
        break;
      default:
        errorMessage = error.message || 'Failed to send reset email';
    }
    
    toast.error(errorMessage);
    throw error;
  }
};

// Logout
export const logoutUser = async () => {
  try {
    await signOut(auth);
    logEvent(analytics, 'logout');
    toast.success("Logged out successfully");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to log out';
    toast.error(errorMessage);
    throw error;
  }
};

// Export Firebase instances
export { auth, db, analytics, storage, functions };
